import * as React from "react";
import {useTheme} from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  Card,
  Divider,
  Grid,
  TextField,
  Paper,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Tooltip
} from "@mui/material";
import {FirstPage, LastPage, NavigateBefore, NavigateNext} from "@mui/icons-material";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {DataGrid, GridActionsCellItem} from "@mui/x-data-grid";
import {GET, POST} from "APIclient";
import {useEffect, useState} from "react";
import {toast} from "react-toastify";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import {Snackbar, Alert} from "@mui/material";
import {Checkbox, FormControlLabel} from "@mui/material";
import {eachDayOfInterval} from "date-fns";
import properties from "constants/ApplicationConstants";

// Custom Pagination Component
function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0"
      }}
    >
      <Typography variant="caption" sx={{color: "#666"}}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
        <Typography variant="caption" sx={{mr: 2, color: "#666"}}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton onClick={handleFirstPage} disabled={page === 0 || loading} size="small">
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton onClick={handlePreviousPage} disabled={page === 0 || loading} size="small">
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor: properties?.colors?.bgcolorbluethem || "#1565c0"
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e"
              }
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const columns = [
  {field: "id", headerName: "#", width: 30, headerAlign: "left", align: "left"},
  {
    field: "empName",
    headerName: "Employee Name",
    width: 130,
    headerAlign: "left",
    align: "left"
  },
  {field: "empId", headerName: "EMP ID", width: 60, headerAlign: "left", align: "left"},
  {
    field: "PL",
    headerName: "PL",
    type: "number",
    width: 50,
    headerAlign: "left",
    align: "left",
    renderCell: params => (
      <div style={{display: "flex", justifyContent: "left", width: "100%"}}>
        {params.value || "-"}
      </div>
    )
  },
  {
    field: "UPL",
    headerName: "UPL",
    type: "number",
    width: 50,
    headerAlign: "left",
    align: "left",
    renderCell: params => (
      <div style={{display: "flex", justifyContent: "left", width: "100%"}}>
        {params.value || "-"}
      </div>
    )
  },
  {
    field: "COFF",
    headerName: "COFF",
    type: "number",
    width: 60,
    headerAlign: "left",
    align: "left",
    renderCell: params => (
      <div style={{display: "flex", justifyContent: "left", width: "100%"}}>
        {params.value || "-"}
      </div>
    )
  },
  {
    field: "LWP",
    headerName: "LWP",
    type: "number",
    width: 50,
    headerAlign: "left",
    align: "left",
    renderCell: params => (
      <div style={{display: "flex", justifyContent: "left", width: "100%"}}>
        {params.value || "-"}
      </div>
    )
  },
  {
    field: "ML",
    headerName: "ML",
    type: "number",
    width: 50,
    headerAlign: "left",
    align: "left",
    renderCell: params => (
      <div style={{display: "flex", justifyContent: "left", width: "100%"}}>
        {params.value || "-"}
      </div>
    )
  },
  {
    field: "TotalDays",
    headerName: "TotalDays",
    type: "number",
    width: 50,
    headerAlign: "left",
    align: "left"
  },
  {
    field: "StartDate",
    headerName: "Start Date",
    width: 100,
    headerAlign: "left",
    align: "left"
  },
  {field: "EndDate", headerName: "End Date", width: 100, headerAlign: "left", align: "left"},
  {
    field: "AppliedOn",
    headerName: "Applied On",
    width: 100,
    headerAlign: "left",
    align: "left"
  },
  {
    field: "status",
    headerName: "Status",
    width: 100,
    headerAlign: "left",
    align: "left",
    renderCell: params => {
      const status = params.value.toLowerCase();
      let buttonColor;
      let statusText;

      switch (status) {
        case "approved":
          buttonColor = "success";
          statusText = "Approved";
          break;
        case "pending":
          buttonColor = "warning";
          statusText = "Pending";
          break;
        case "cancelled":
          buttonColor = "secondary";
          statusText = "Cancelled";
          break;
        case "rejected":
          buttonColor = "error";
          statusText = "Rejected";
          break;
        default:
          buttonColor = "secondary";
          statusText = "Unknown";
      }

      return (
        <MDButton
          variant="contained"
          color={buttonColor}
          size="small"
          disabled
          sx={{
            textTransform: "capitalize",
            fontSize: "0.800rem",
            width: "70px",
            justifyContent: "center"
          }}
        >
          {statusText}
        </MDButton>
      );
    }
  },
  {
    field: "actions",
    headerName: "Action",
    width: 110,
    headerAlign: "center",
    align: "center",
    renderCell: params => {
      const status = params.row.status.toLowerCase();
      const isActionDisabled =
        status === "approved" ||
        status === "cancelled" ||
        status === "rejected" ||
        status === "reject";
      const isRejectDisabled = !(status === "pending" || status === "approved");

      return [
        <Tooltip title="Approve Leave" arrow key={`approve-${params.id}`}>
          <GridActionsCellItem
            icon={<DoneIcon />}
            label="Approve"
            color="success"
            onClick={() => params.row.handleApprove(params.row)}
            disabled={isActionDisabled}
          />
        </Tooltip>,
        <Tooltip title="Reject Leave" arrow key={`reject-${params.id}`}>
          <GridActionsCellItem
            icon={<ClearIcon />}
            label="Reject"
            color="error"
            onClick={() => params.row.handleReject(params.row)}
            disabled={isRejectDisabled}
          />
        </Tooltip>,
        <Tooltip title="View Details" arrow key={`view-${params.id}`}>
          <GridActionsCellItem
            icon={<VisibilityIcon />}
            label="View"
            color="primary"
            onClick={() => params.row.handleView(params.row)}
          />
        </Tooltip>
      ];
    }
  }
];

function getLeaveDates(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const dateArray = eachDayOfInterval({start, end});
  return dateArray.map(date => dayjs(date).format("YYYY-MM-DD"));
}

export default function AdminLeaveRequest() {
  const empId = Number(window.sessionStorage.getItem("empId"));
  const theme = useTheme();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filteredLeaveRequests, setFilteredLeaveRequests] = useState([]);
  const [paginationModel, setPaginationModel] = React.useState({page: 0, pageSize: 20});
  const [totalItems, setTotalItems] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [actionType, setActionType] = useState("");
  const [reason, setReason] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openApproveRejectModal, setOpenApproveRejectModal] = useState(false);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [allDates, setAllDates] = useState([]);
  const [updateFlag, setUpdateFlag] = useState(false);

  const [searchParams, setSearchParams] = useState({
    empId: "",
    empName: "",
    status: "",
    startDate: null,
    endDate: null
  });

  const handleClose = () => {
    setSnackOpen(false);
  };

  const resetLeaveSelection = () => {
    setSelectedDates([]);
    setAllDates([]);
    setSelectAll(false);
    setReason("");
  };

  useEffect(() => {
    if (!selectedLeaveRequest) {
      setAllDates([]);
      setSelectedDates([]);
      setSelectAll(false);
      return;
    }

    let leaveDates = [];

    if (selectedLeaveRequest.leaveDates) {
      leaveDates = selectedLeaveRequest.leaveDates
        .split(",")
        .map(date => dayjs(date.trim()).format("YYYY-MM-DD"));
    } else {
      const {StartDate, EndDate} = selectedLeaveRequest;

      leaveDates = getLeaveDates(StartDate, EndDate).filter(date => {
        const day = new Date(date).getDay();
        return day !== 0 && day !== 6;
      });
    }

    setAllDates(leaveDates);

    setSelectedDates([]);

    if (leaveDates.length === 1) {
      setSelectedDates([leaveDates[0]]);
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedLeaveRequest]);

  const handleDateChange = date => {
    setSelectedDates(prevSelectedDates =>
      prevSelectedDates.includes(date)
        ? prevSelectedDates.filter(d => d !== date)
        : [...prevSelectedDates, date]
    );
  };

  const handleSelectAllChange = event => {
    if (event.target.checked) {
      setSelectedDates(allDates);
    } else {
      setSelectedDates([]);
    }
    setSelectAll(event.target.checked);
  };

  const handleViewDetails = leaveRequest => {
    setSelectedLeaveRequest(leaveRequest);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedLeaveRequest(null);
  };

  const handleApproveOpen = leaveRequest => {
    resetLeaveSelection();
    setActionType("approve");
    setSelectedLeaveRequest(leaveRequest);
    setOpenApproveRejectModal(true);
  };

  const handleRejectOpen = leaveRequest => {
    resetLeaveSelection();
    setActionType("reject");
    setSelectedLeaveRequest(leaveRequest);
    setOpenApproveRejectModal(true);
  };

  const handleCancelOpen = leaveRequest => {
    resetLeaveSelection();

    setActionType("cancel");
    setSelectedLeaveRequest(leaveRequest);
    setOpenApproveRejectModal(true);
  };

  const handleApproveRejectClose = () => {
    setOpenApproveRejectModal(false);
    setSelectedLeaveRequest(null);
    setReason("");
    resetLeaveSelection();
  };

  const handleApproveRejectSubmit = async () => {
    if (!reason) {
      setSnackMessage("Please provide a reason.");
      setSnackColor("error");
      setSnackOpen(true);
      return;
    }

    try {
      const url = `manager/${actionType}/${selectedLeaveRequest.leaveRequestId}/${selectedLeaveRequest.empId}`;
      const statusCode = actionType === "approve" ? 1 : 2;
      const payload = {
        loggedInEmpId: null,
        reason: reason,
        leaveDates: selectedDates,
        status: statusCode
      };

      const response = await POST(url, payload);
      if (response.status === 200) {
        setSnackMessage(response.data.message);
        setSnackColor("success");
        setSnackOpen(true);
        updateLeaveRequestStatus(
          selectedLeaveRequest.leaveRequestId,
          actionType === "approve" ? "Approved" : "Rejected"
        );
        handleApproveRejectClose();
        setUpdateFlag(prev => !prev);
      }
    } catch (error) {
      setSnackMessage(
        error.response?.data?.message ||
          `Error ${actionType === "approve" ? "approving" : "rejecting"} the leave request.`
      );
      setSnackColor("error");
      setSnackOpen(true);
    }
  };

  const handleCancelLeave = async () => {
    if (!reason) {
      setSnackMessage("Please provide a reason for canceling.");
      setSnackColor("error");
      setSnackOpen(true);
      return;
    }
    try {
      const url = `manager/cancelLeave/${selectedLeaveRequest.leaveRequestId}/${selectedLeaveRequest.empId}`;
      const payload = {
        loggedInEmpId: null,
        reason: reason,
        status: 4
      };

      const response = await POST(url, payload);
      if (response.status === 200) {
        setSnackMessage(response.data.message);
        setSnackColor("success");
        setSnackOpen(true);
        updateLeaveRequestStatus(selectedLeaveRequest.leaveRequestId, "Cancelled");
        handleApproveRejectClose();
        setUpdateFlag(prev => !prev);
      }
    } catch (error) {
      setSnackMessage(error.response?.data?.message || "Error canceling the leave request.");
      setSnackColor("error");
      setSnackOpen(true);
    }
  };

  const updateLeaveRequestStatus = (leaveRequestId, status) => {
    setLeaveRequests(prev =>
      prev.map(req => (req.leaveRequestId === leaveRequestId ? {...req, status} : req))
    );
    setFilteredLeaveRequests(prev =>
      prev.map(req => (req.leaveRequestId === leaveRequestId ? {...req, status} : req))
    );
  };

  const fetchLeaveRequests = async (page, pageSize, searchParams = {}) => {
    if (isFetching) return;
    setIsFetching(true);
    try {
      const startDateFormatted = searchParams.startDate
        ? dayjs(searchParams.startDate).format("YYYY-MM-DD")
        : "";
      const endDateFormatted = searchParams.endDate
        ? dayjs(searchParams.endDate).format("YYYY-MM-DD")
        : "";

      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        empId: searchParams.empId || "",
        empName: searchParams.empName || "",
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        managerId: Number(window.sessionStorage.getItem("empId")) || ""
      });

      if (searchParams.status) {
        if (searchParams.status === "Pending") {
          queryParams.append("status", "1");
          // queryParams.append("approved", "0");
        } else if (searchParams.status === "Approved") {
          queryParams.append("approved", "1");
          queryParams.append("status", "0");
        } else if (searchParams.status === "Rejected") {
          queryParams.append("status", "2");
        } else if (searchParams.status === "Cancelled") {
          queryParams.append("status", "0");
        }
      }

      const url = `admin/leave-requests/search?${queryParams.toString()}`;
      const response = await GET(url);

      if (response.status === 200) {
        const data = response.data.data.paginatedData || [];
        const totalRecords = response.data.data.totalItems || 0;
        const totalPages = response.data.data.totalPages || 0;

        let index = page * pageSize + 1;
        const formattedData = data.map(item => {
          let statusLabel = " ";
          let startDate = dayjs(item.startDate).format("DD MMM YYYY");
          let endDate = item.endDate ? dayjs(item.endDate).format("DD MMM YYYY") : startDate;
          if (item.status === 1) {
            statusLabel = "Pending";
          } else if (item.approved === 1 && item.status === 0) {
            statusLabel = "Approved";
          } else if (item.status === 2) {
            statusLabel = "Rejected";
          } else if ((item.cancel === 1 && item.approved === 1) || item.cancel === 1) {
            statusLabel = "Cancelled";
          }

          const plannedLeaveDays = item.leaveType === "PL" ? item.totalDays : 0;
          const unplannedLeaveDays = item.leaveType === "UPL" ? item.totalDays : 0;
          const coffLeaveDays = item.leaveType === "COFF" ? item.totalDays : 0;
          const lwpLeaveDays = item.leaveType === "LWP" ? item.totalDays : 0;
          const maternityLeave = item.leaveType === "MaternityLeave" ? item.totalDays : 0;
          const onsiteLeave = item.leaveType === "Onsite" ? item.totalDays : 0;
          const totalLeaveDays =
            plannedLeaveDays + unplannedLeaveDays + coffLeaveDays + lwpLeaveDays + maternityLeave;

          return {
            id: index++,
            empId: item.empId,
            empName: `${item.firstName} ${item.lastName}`,
            StartDate: startDate,
            EndDate: endDate,
            PL: plannedLeaveDays || "-",
            UPL: unplannedLeaveDays || "-",
            COFF: coffLeaveDays || "-",
            LWP: lwpLeaveDays || "-",
            ML: maternityLeave || "-",
            Onsite: onsiteLeave || "-",
            AppliedOn: dayjs(item.addedOn).format("DD MMM YYYY"),
            TotalDays: item.totalDays,
            leaveType: item.leaveType || "No Leave Type",
            status: statusLabel,
            reason: item.reason,
            leaveRequestId: item.leaveRequestId,
            leaveDates: item.leaveDates,
            handleApprove: handleApproveOpen,
            handleReject: handleRejectOpen,
            handleCancel: handleCancelOpen,
            handleView: handleViewDetails
          };
        });

        setLeaveRequests(formattedData);
        setFilteredLeaveRequests(formattedData);
        setTotalItems(totalRecords);
        setTotalPages(totalPages);
      } else {
        setSnackMessage("Failed to fetch leave requests");
        setSnackColor("error");
        setSnackOpen(true);
      }
    } catch (error) {
      setSnackMessage(error.response?.data?.message || "Error fetching leave requests.");
      setSnackColor("error");
      setSnackOpen(true);
    } finally {
      setIsFetching(false);
    }
  };

  const handleSearch = () => {
    setPaginationModel({page: 0, pageSize: paginationModel.pageSize});
    fetchLeaveRequests(0, paginationModel.pageSize, searchParams);
  };

  const handleSearchChange = event => {
    const {name, value} = event.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Updated pagination handlers
  const handlePageChange = newPage => {
    const updatedPaginationModel = {...paginationModel, page: newPage};
    setPaginationModel(updatedPaginationModel);
    fetchLeaveRequests(newPage, paginationModel.pageSize, searchParams);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      const updatedPaginationModel = {...paginationModel, page: lastPageIndex};
      setPaginationModel(updatedPaginationModel);
      await fetchLeaveRequests(lastPageIndex, paginationModel.pageSize, searchParams);
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  };

  useEffect(() => {
    fetchLeaveRequests(paginationModel.page, paginationModel.pageSize, searchParams);
  }, [empId, updateFlag]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox container pt={2} pb={2} px={3}>
              <MDBox sx={{display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 1.5}}>
                <Typography
                  sx={{
                    gridColumn: "span 2",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Leave Requests
                </Typography>
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchParams.empId}
                  onChange={handleSearchChange}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="empId"
                  label="Employee ID"
                  variant="outlined"
                />
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchParams.empName}
                  onChange={handleSearchChange}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="empName"
                  label="Employee Name"
                  variant="outlined"
                />
                <FormControl sx={{gridColumn: "span 2"}}>
                  <InputLabel id="status-select-label">Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    value={searchParams.status}
                    label="Status"
                    name="status"
                    onChange={handleSearchChange}
                    variant="outlined"
                    sx={{
                      height: "45px",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Approved">Approved</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
                <MDButton
                  sx={{
                    gridColumn: "span 2",
                    background: properties.colors.bgcolorbluethem
                  }}
                  variant="gradient"
                  color="info"
                  onClick={handleSearch}
                >
                  Search
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>
        <MDBox>
          <Paper>
            <DataGrid
              rows={filteredLeaveRequests}
              columns={columns}
              paginationMode="server"
              rowCount={totalItems}
              pageSizeOptions={[20]}
              paginationModel={paginationModel}
              loading={isFetching}
              hideFooter
              size="small"
              sx={{
                height: "450px",
                fontFamily: properties.fontFamily,
                fontSize: "14px",
                fontWeight: 400,
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important"
                },
                "& .MuiDataGrid-cell": {
                  textAlign: "left"
                }
              }}
            />
            <CustomPagination
              page={paginationModel.page}
              pageSize={paginationModel.pageSize}
              totalRows={totalItems}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={isFetching}
            />
          </Paper>
        </MDBox>

        {/* View Details Modal */}
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              width: "700px",
              maxWidth: "none"
            }
          }}
        >
          <DialogTitle
            sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}
          >
            <MDTypography variant="h6" sx={{fontFamily: properties.fontFamily}}>
              Leave Application Details
            </MDTypography>
          </DialogTitle>
          <DialogContent sx={{padding: 2}}>
            {selectedLeaveRequest && (
              <form>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee ID"
                      value={selectedLeaveRequest.empId || "No ID Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee Name"
                      value={selectedLeaveRequest.empName || "No Name Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Applied On"
                      value={selectedLeaveRequest.AppliedOn || "No Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Start Date"
                      value={selectedLeaveRequest.StartDate || "No Start Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="End Date"
                      value={selectedLeaveRequest.EndDate || "No End Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Leave Type"
                      value={selectedLeaveRequest.leaveType || "No Leave Type Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Status"
                      value={selectedLeaveRequest.status || "No status available"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Reason"
                      value={selectedLeaveRequest.reason}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Divider sx={{my: 2}} />
                </Grid>
              </form>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseModal} variant="contained">
              Close
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Approve/Reject/Cancel Modal */}
        <Dialog
          open={openApproveRejectModal}
          onClose={handleApproveRejectClose}
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              width: "700px",
              maxWidth: "none"
            }
          }}
        >
          <DialogTitle>
            <MDTypography variant="h6" sx={{fontFamily: properties.fontFamily}}>
              {actionType === "approve" ? "Approve" : actionType === "reject" ? "Reject" : "Cancel"}{" "}
              Leave Request
            </MDTypography>
          </DialogTitle>
          <DialogContent sx={{padding: 2}}>
            {selectedLeaveRequest && (
              <form>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee ID"
                      value={selectedLeaveRequest.empId || "No ID Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee Name"
                      value={selectedLeaveRequest.empName || "No Name Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Applied On"
                      value={selectedLeaveRequest.AppliedOn || "No Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Leave Type"
                      value={selectedLeaveRequest.leaveType || "No Leave Type Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Status"
                      value={selectedLeaveRequest.status || "No status available"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <div>
                      <FormControlLabel
                        control={<Checkbox checked={selectAll} onChange={handleSelectAllChange} />}
                        label="Select All Dates"
                      />
                      <Grid container spacing={1} justifyContent="flex-start">
                        {allDates &&
                          allDates.map((date, index) => (
                            <Grid
                              item
                              xs={3}
                              key={index}
                              container
                              alignItems="center"
                              justifyContent="flex-start"
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedDates.includes(date)}
                                    onChange={() => handleDateChange(date)}
                                  />
                                }
                                label={dayjs(date).format("DD-MM-YYYY")}
                              />
                            </Grid>
                          ))}
                      </Grid>
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={
                        <span>
                          Reason<span style={{color: properties.colors.Red}}>*</span>
                        </span>
                      }
                      multiline
                      rows={1}
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                    />
                  </Grid>
                </Grid>
                <Divider sx={{my: 2}} />
              </form>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleApproveRejectClose} variant="contained">
              Cancel
            </MDButton>
            <MDButton
              color="primary"
              onClick={actionType === "cancel" ? handleCancelLeave : handleApproveRejectSubmit}
              variant="contained"
            >
              Submit
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackOpen}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity={snackColor === "success" ? "success" : "error"}
            variant="filled"
            sx={{width: "100%", color: "#fff"}}
          >
            {snackMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
}
